import { Button, Table, Modal, message, Col, Row } from 'antd';
import React, { useEffect, useState } from 'react';

import IconUpload from 'assets/images/IconUpload.svg';
import IconDelete from 'assets/images/IconDelete.svg';
import IconEdit from 'assets/images/IconEdit.svg';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Form, Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';

import { updateClass, getClass, addTeacherClass, deleteClassMembers, uploadStudentsToClass } from 'requests/class';
import { getListTeacherDropdown } from 'requests/teacher';
import { getListStudentDropdown, addStudentClass } from 'requests/student';
import _ from 'lodash';

const { confirm } = Modal;

const EditClass = () => {
    const [loading, setLoading] = useState(false);
    const [classData, setClassData] = useState();
    const [teachers, setTeachers] = useState([]);
    const [teachersTake, setTeachersTake] = useState(20);
    const [teacherTotal, setTeacherTotal] = useState(21);
    const [teacherSearchKeyword, setTeacherSearchKeyword] = useState('');
    const [students, setStudents] = useState([]);
    const [studentsTake, setStudentsTake] = useState(20);
    const [studentTotal, setStudentTotal] = useState(21);
    const [studentSearch, setStudentSearch] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [query, setQuery] = useState({
        page: 1,
        pageSize: 10,
    });
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [selectedTeacher, setSelectedTeacher] = useState();

    const { t } = useTranslation();
    const params = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
        },
        {
            title: t('student_name'),
            dataIndex: 'name',
            render: (text, record, index) => (
                <Link to={`/students/${record.id}/edit`} type="link">
                    {text}
                </Link>
            ),
        },
        {
            title: t('email'),
            dataIndex: 'email',
        },
        {
            title: t('mobile'),
            dataIndex: 'mobile',
        },
        {
            title: t('action'),
            render: (text, record, index) => (
                <div className="nowrap">
                    <Link to={`/students/${record.id}/edit`} type="link">
                        <img src={IconEdit} alt="" />
                    </Link>
                    <Button type="link" onClick={() => handleClickDeleteBtn(record.id)}>
                        <img src={IconDelete} alt="" />
                    </Button>
                </div>
            ),
        },
    ];

    useEffect(() => {
        getData();

        return () => {
            sessionStorage.setItem('is_form_changed', 0);
        };
    }, []);

    const getData = async () => {
        try {
            setLoading(true);
            const data = await getClass(params.id);

            setSelectedTeacher(data.teachers[0]?.id);
            setClassData(data);
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedTeacher && teachers.find((e) => e.value === selectedTeacher)) {
            form.setFieldsValue({ teachers: selectedTeacher });
        }
    }, [classData, teachers]);

    useEffect(() => {
        setLoading(true);
        const params = { role: 2, take: teachersTake };
        if (teacherSearchKeyword) params.search = teacherSearchKeyword;
        getListTeacherDropdown(params)
            .then((data) => {
                const teacherOptions = data.records.map((record) => {
                    return { label: record.name, value: record.id };
                });
                if (classData?.teachers[0] && !teacherOptions.find((e) => e.value === classData?.teachers[0].id)) {
                    teacherOptions.unshift({
                        value: classData.teachers[0].id,
                        label: classData.teachers[0].name,
                    });
                }
                setTeachers(teacherOptions);
                setTeacherTotal(data.total);
            })
            .catch((err) => {
                console.log(err);
                message.error(t(err.response?.data.message || 'error'));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [teachersTake, classData, teacherSearchKeyword]);

    useEffect(() => {
        setLoading(true);
        getListStudentDropdown({ take: studentsTake, search: studentSearch })
            .then((data) => {
                const studentOptions = data.records
                    .filter((record) => !record.classes.length)
                    .map((record) => {
                        return { label: record.name, value: record.id };
                    });

                setStudents(studentOptions);
                setStudentTotal(data.total);
            })
            .catch((err) => {
                console.log(err);
                message.error(t(err.response?.data.message || 'error'));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [studentsTake, classData, studentSearch]);

    const handleTeacherScroll = (e) => {
        const target = e.target;
        if (
            !loading &&
            teachersTake < teacherTotal &&
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 10
        ) {
            setTeachersTake(teachersTake + 10);
        }
    };

    const handleStudentScroll = (e) => {
        const target = e.target;
        if (
            !loading &&
            studentsTake < studentTotal &&
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 10
        ) {
            setStudentsTake(studentsTake + 10);
        }
    };

    const showEditConfirm = () => {
        handleEditClass(params.id);
    };

    const handleEditClass = async (id) => {
        try {
            const values = await form.validateFields();
            for (const key in values) {
                if (typeof values[key] === 'string') values[key] = values[key].trim();
            }
            form.setFieldsValue(values);

            delete values.teachers;
            delete values.studentsCount;
            const newClassData = await updateClass(id, values);
            setClassData(newClassData);
            message.success(t('success'));
            sessionStorage.setItem('is_form_changed', 0);
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        }
    };

    const handleClickDeleteBtn = (id) => {
        // modal confirm
        confirm({
            title: t('confirm_delete_many'),
            icon: <ExclamationCircleOutlined />,
            cancelText: t('cancel'),
            onOk() {
                handleDelete(id);
            },
        });
    };

    const handleChangePage = (page, pageSize) => {
        setQuery((query) => ({
            ...query,
            page,
            pageSize,
        }));
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
    };

    const handleAddTeacherClass = async (id) => {
        try {
            const values = await form.validateFields();

            const assignData = { memberIds: [values.teachers] };
            await addTeacherClass(params.id, assignData);

            classData.teachers = [{ id: values.teachers }];
            await getData();
            handleFormChange();
            message.success(t('success'));
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        }
    };

    const handleAddStudentClass = async () => {
        try {
            await addStudentClass(params.id, { memberIds: [selectedStudent] });
            message.success(t('success'));
            getData();
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteClassMembers(params.id, { memberIds: [id] });
            getData();
            message.success(t('success'));
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        }
    };

    const handleDeleteMany = async () => {
        try {
            await deleteClassMembers(params.id, { memberIds: selectedRowKeys });

            setSelectedRowKeys([]);
            message.success(t('success'));

            getData();
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        }
    };

    let handleUpload = (e) => {
        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('list', file);

        setLoading(true);
        uploadStudentsToClass(params.id, formData)
            .then(() => {
                getData();
                message.success(t('success'));
            })
            .catch((err) => {
                console.log(err);
                const errors = err.response?.data.response;
                if (typeof errors === 'object') {
                    for (const key in errors) {
                        message.error(`${key}: ${t(errors[key])}`);
                    }
                } else {
                    message.error(t(errors));
                }
            })
            .finally(() => {
                setLoading(false);
                e.target.value = '';
            });
    };

    const handleSearchTeacher = _.debounce(setTeacherSearchKeyword, 400);

    const handleStudentSearch = _.debounce(setStudentSearch, 400);

    const handleFormChange = () => {
        const formValues = form.getFieldsValue(true);
        const classDataCompare = {
            ...classData,
            teachers: classData.teachers[0].id || null,
        };

        if (_.isEqual(classDataCompare, formValues)) {
            sessionStorage.setItem('is_form_changed', 0);
        } else {
            sessionStorage.setItem('is_form_changed', 1);
        }
    };

    return (
        classData && (
            <div className="class-view">
                <div className="edit-class-form">
                    <h1 className="mt-24 mb-24">Edit Class</h1>

                    <div className="add-input">
                        <Form
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 16 }}
                            layout="horizontal"
                            form={form}
                            initialValues={{ ...classData, teachers: null }}
                            onValuesChange={handleFormChange}
                        >
                            <Row>
                                <Col span={12}>
                                    <Form.Item
                                        name="className"
                                        label={t('class_name')}
                                        rules={[{ required: true, pattern: /\S+/, message: t('required_field') }]}
                                    >
                                        <Input placeholder={t('input_name_add')} />
                                    </Form.Item>
                                </Col>
                                <Col span={12} className="select-teacher">
                                    <Form.Item label={t('teacher_name')} className="teacher-input">
                                        <Form.Item name="teachers" style={{ flex: 1 }}>
                                            <Select
                                                showSearch
                                                filterOption={false}
                                                className="teacher-select-input"
                                                style={{ width: '100%' }}
                                                placeholder={t('select_teacher')}
                                                optionLabelProp="label"
                                                options={teachers}
                                                onPopupScroll={handleTeacherScroll}
                                                onChange={setSelectedTeacher}
                                                onSearch={handleSearchTeacher}
                                                onDropdownVisibleChange={() => setTeacherSearchKeyword('')}
                                            ></Select>
                                        </Form.Item>
                                        <Button className="change-btn" onClick={handleAddTeacherClass}>
                                            {t('change')}
                                        </Button>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label={t('student_number')}>
                                        <Input value={classData.studentsCount} disabled />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                        <div className="add-edit-btn">
                            <Button onClick={() => navigate(-1)} className="ant-btn cancel-btn">
                                {t('cancel')}
                            </Button>
                            <Button className="ant-btn save-btn" onClick={showEditConfirm}>
                                {t('save')}
                            </Button>
                        </div>
                        <div className="search-box">
                            <div className="select-add-student-class">
                                <Select
                                    showSearch
                                    filterOption={false}
                                    placeholder={t('add_student')}
                                    onChange={setSelectedStudent}
                                    optionLabelProp="label"
                                    options={students}
                                    onPopupScroll={handleStudentScroll}
                                    onSearch={handleStudentSearch}
                                    onDropdownVisibleChange={() => setStudentSearch('')}
                                ></Select>
                                <Button
                                    type="default"
                                    danger
                                    className="add-student-class-btn"
                                    onClick={handleAddStudentClass}
                                    disabled={!selectedStudent}
                                >
                                    {t('add_new')}
                                </Button>
                            </div>

                            <Button
                                type="default"
                                danger
                                className="delete-btn"
                                onClick={handleDeleteMany}
                                disabled={!selectedRowKeys.length}
                            >
                                {t('delete')}
                            </Button>
                            <label>
                                <input type="file" onInput={handleUpload} style={{ display: 'none' }} />
                                <div
                                    type="primary"
                                    className="ant-btn ant-btn-primary ant-btn-dangerous upload-btn ml-24"
                                >
                                    <img src={IconUpload} alt="" />
                                    <span>{t('upload')}</span>
                                </div>
                            </label>
                        </div>
                        <div className="table-student">
                            <Table
                                rowKey="id"
                                rowSelection={rowSelection}
                                columns={columns}
                                dataSource={classData.students}
                                pagination={{
                                    pageSize: query.pageSize,
                                    total: classData.students.length,
                                    current: query.page,
                                    onChange: (page, pageSize) => handleChangePage(page, pageSize),
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        )
    );
};

export default EditClass;
